import { Outlet, Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  User,
  Briefcase,
  Calendar,
  Inbox,
  FileText,
  MessageSquare,
  CreditCard,
  Settings,
  Bell,
  LogOut,
  ChevronRight,
  BookOpen,
  DollarSign,
  Users,
  Star,
  BarChart3,
  Menu,
  X,
  FilePlus,
  Search,
  Bookmark,
  FolderOpen,
  ShieldCheck,
  AlertTriangle,
  TrendingUp,
  Building2,
  Award,
  ChevronDown,
  SwitchCamera,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { CurrencySelector } from '@/components/currency';
import { GlobalSearchBar } from '@/components/search';
import { GradientAvatar, getGradientForId, getInitials } from '@/components/ui/GradientAvatar';
import { useState, useEffect } from 'react';

type UserRole = 'company' | 'cxo' | 'member' | 'admin';

interface NavItem {
  path: string;
  label: string;
  icon: React.ElementType;
  badge?: number;
}

interface PortalConfig {
  role: UserRole;
  label: string;
  color: string;
  icon: React.ElementType;
  items: NavItem[];
}

export default function UnifiedLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isLoading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showRoleSwitcher, setShowRoleSwitcher] = useState(false);

  // Wait for auth to finish loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate">Loading...</p>
        </div>
      </div>
    );
  }

  // Get user's roles
  const userRoles = (user?.roles || [user?.role].filter(Boolean)) as UserRole[] || [];

  // Determine initial active role based on current path
  const getRoleFromPath = (path: string): UserRole | null => {
    if (path.startsWith('/admin')) return 'admin';
    if (path.startsWith('/company')) return 'company';
    if (path.startsWith('/cxo')) return 'cxo';
    if (path.startsWith('/member')) return 'member';
    return null;
  };

  // Get default role for user
  const getDefaultRole = (): UserRole => {
    if (userRoles.includes('admin')) return 'admin';
    if (userRoles.includes('company')) return 'company';
    if (userRoles.includes('cxo')) return 'cxo';
    return 'member';
  };

  // Active role state (persisted to localStorage)
  const [activeRole, setActiveRole] = useState<UserRole>(() => {
    const saved = localStorage.getItem('cxo-active-role');
    const fromPath = getRoleFromPath(location.pathname);
    
    // Use path role if valid, otherwise use saved, otherwise default
    if (fromPath && userRoles.includes(fromPath)) return fromPath;
    if (saved && userRoles.includes(saved as UserRole)) return saved as UserRole;
    return getDefaultRole();
  });

  // Persist active role
  useEffect(() => {
    localStorage.setItem('cxo-active-role', activeRole);
  }, [activeRole]);

  // Portal configurations
  const portals: Record<UserRole, PortalConfig> = {
    admin: {
      role: 'admin',
      label: 'Admin Console',
      color: 'bg-error',
      icon: ShieldCheck,
      items: [
        { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { path: '/admin/verification', label: 'Verification Queue', icon: ShieldCheck },
        { path: '/admin/users', label: 'Users', icon: Users },
        { path: '/admin/cxos', label: 'CXOs', icon: Award },
        { path: '/admin/companies', label: 'Companies', icon: Building2 },
        { path: '/admin/briefs', label: 'Briefs', icon: FileText },
        { path: '/admin/engagements', label: 'Engagements', icon: Briefcase },
        { path: '/admin/disputes', label: 'Disputes', icon: AlertTriangle },
        { path: '/admin/payouts', label: 'Payouts', icon: DollarSign },
        { path: '/admin/content', label: 'Content', icon: BookOpen },
        { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
        { path: '/admin/settings', label: 'Settings', icon: Settings },
      ],
    },
    company: {
      role: 'company',
      label: 'Company Portal',
      color: 'bg-success',
      icon: Building2,
      items: [
        { path: '/company/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { path: '/company/briefs/new', label: 'Create Brief', icon: FilePlus },
        { path: '/company/cxos', label: 'Find CXOs', icon: Search },
        { path: '/company/shortlist', label: 'Shortlist', icon: Bookmark },
        { path: '/company/engagements', label: 'Engagements', icon: Briefcase },
        { path: '/company/analytics', label: 'Analytics', icon: BarChart3 },
        { path: '/company/documents', label: 'Documents', icon: FolderOpen },
        { path: '/company/billing', label: 'Billing', icon: CreditCard },
      ],
    },
    cxo: {
      role: 'cxo',
      label: 'CXO Portal',
      color: 'bg-teal',
      icon: Award,
      items: [
        { path: '/cxo/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { path: '/cxo/services', label: 'Services', icon: Briefcase },
        { path: '/cxo/availability', label: 'Availability', icon: Calendar },
        { path: '/cxo/leads', label: 'Leads', icon: Inbox },
        { path: '/cxo/proposals', label: 'Proposals', icon: FileText },
        { path: '/cxo/engagements', label: 'Engagements', icon: Briefcase },
        { path: '/cxo/analytics', label: 'Analytics', icon: BarChart3 },
        { path: '/cxo/invoicing', label: 'Invoicing', icon: CreditCard },
      ],
    },
    member: {
      role: 'member',
      label: 'Member Portal',
      color: 'bg-slate',
      icon: User,
      items: [
        { path: '/member/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { path: '/cxos', label: 'Discover CXOs', icon: Search },
        { path: '/feed', label: 'My Feed', icon: BookOpen },
        { path: '/articles/my', label: 'My Articles', icon: FileText },
        { path: '/subscribers', label: 'Subscribers', icon: Users },
        { path: '/reviews', label: 'Reviews', icon: Star },
        { path: '/insights/revenue', label: 'Revenue', icon: DollarSign },
      ],
    },
  };

  // Shared items visible to all roles
  const sharedItems: NavItem[] = [
    { path: '/messages', label: 'Inbox', icon: MessageSquare },
    { path: '/profile', label: 'Profile', icon: User },
    { path: '/settings', label: 'Settings', icon: Settings },
  ];

  // Switch active role and navigate to that role's dashboard
  const handleRoleSwitch = (role: UserRole) => {
    setActiveRole(role);
    setShowRoleSwitcher(false);
    
    // Navigate to the appropriate dashboard for the new role
    const dashboardPaths: Record<UserRole, string> = {
      admin: '/admin/dashboard',
      company: '/company/dashboard',
      cxo: '/cxo/dashboard',
      member: '/member/dashboard',
    };
    
    navigate(dashboardPaths[role]);
  };

  // Close sidebar when route changes on mobile
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (sidebarOpen && !target.closest('.sidebar') && !target.closest('.menu-btn')) {
        setSidebarOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sidebarOpen]);

  // If user has no valid roles, redirect to login
  if (!user || userRoles.length === 0) {
    return <Navigate to="/login" replace />;
  }

  const currentPortal = portals[activeRole];

  const ActiveIcon = currentPortal?.icon || Award;

  // If currentPortal is undefined, something is wrong with activeRole
  if (!currentPortal) {
    // Reset to default role
    const defaultRole = getDefaultRole();
    console.log('[UnifiedLayout] Resetting to defaultRole:', defaultRole);
    setActiveRole(defaultRole);
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate">Loading portal...</p>
        </div>
      </div>
    );
  }

  // Get page title from current nav items
  const allItems = [...currentPortal.items, ...sharedItems];
  const currentNavItem = allItems.find(item => 
    location.pathname === item.path || location.pathname.startsWith(item.path + '/')
  );
  const pageTitle = currentNavItem?.label || currentPortal.label;

  return (
    <div className="min-h-screen bg-navy flex">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`sidebar w-72 bg-navy-light border-r border-white/10 flex-shrink-0 fixed h-full overflow-y-auto z-50 transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Logo */}
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <span className="font-heading text-xl font-bold text-white">
              CxO <span className="text-teal">On Demand</span>
            </span>
          </Link>
          <button 
            className="lg:hidden text-slate hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Role Switcher */}
        <div className="p-4 border-b border-white/10">
          <label className="text-xs font-medium text-slate uppercase tracking-wider mb-2 block">
            Viewing as
          </label>
          <div className="relative">
            <button
              onClick={() => setShowRoleSwitcher(!showRoleSwitcher)}
              className="w-full flex items-center gap-3 px-3 py-2.5 bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
            >
              <div className={`w-8 h-8 ${currentPortal.color} rounded-lg flex items-center justify-center`}>
                <ActiveIcon className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 text-left">
                <span className="text-sm font-medium text-white block">
                  {currentPortal.label}
                </span>
              </div>
              {userRoles.length > 1 && (
                <>
                  <SwitchCamera className="w-4 h-4 text-slate" />
                  <ChevronDown className={`w-4 h-4 text-slate transition-transform ${showRoleSwitcher ? 'rotate-180' : ''}`} />
                </>
              )}
            </button>

            {/* Role Switcher Dropdown */}
            {showRoleSwitcher && userRoles.length > 1 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-navy border border-white/10 rounded-xl shadow-xl z-50 overflow-hidden">
                {userRoles.filter(role => role !== activeRole).map((role) => {
                  const portal = portals[role];
                  const Icon = portal.icon;
                  return (
                    <button
                      key={role}
                      onClick={() => handleRoleSwitch(role)}
                      className="w-full flex items-center gap-3 px-3 py-3 hover:bg-white/5 transition-colors text-left"
                    >
                      <div className={`w-8 h-8 ${portal.color} rounded-lg flex items-center justify-center`}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-sm text-slate hover:text-white">
                        Switch to {portal.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Portal Navigation */}
        <nav className="p-4">
          <div className="space-y-1">
            {currentPortal.items.map((item) => {
              const isActive = location.pathname === item.path || 
                               location.pathname.startsWith(item.path + '/');
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 relative overflow-hidden ${
                    isActive
                      ? 'bg-teal/10 text-teal'
                      : 'text-slate hover:bg-white/5 hover:text-white'
                  }`}
                >
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-teal rounded-r-full" />
                  )}
                  <Icon className={`w-5 h-5 ${isActive ? 'text-teal' : ''}`} />
                  <span className="text-sm font-medium">{item.label}</span>
                  {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Divider */}
        <div className="mx-4 border-t border-white/10" />

        {/* Shared Navigation */}
        <nav className="p-4">
          <label className="text-xs font-medium text-slate uppercase tracking-wider mb-2 block">
            Account
          </label>
          <div className="space-y-1">
            {sharedItems.map((item) => {
              const isActive = location.pathname === item.path || 
                               location.pathname.startsWith(item.path + '/');
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 relative overflow-hidden ${
                    isActive
                      ? 'bg-white/10 text-white'
                      : 'text-slate hover:bg-white/5 hover:text-white'
                  }`}
                >
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-white/50 rounded-r-full" />
                  )}
                  <Icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>

        {/* User Section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10 bg-navy-light">
          <div className="flex items-center gap-3 mb-3">
            <GradientAvatar
              firstName={user?.firstName}
              lastName={user?.lastName}
              userId={user?.id}
              src={user?.avatar}
              size="md"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-slate truncate">
                {user?.email}
              </p>
            </div>
          </div>          
          <button
            onClick={logout}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate hover:text-white hover:bg-white/5 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-72">
        {/* Header */}
        <header className="h-16 border-b border-white/10 bg-navy/80 backdrop-blur-md sticky top-0 z-30 px-4 lg:px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              className="menu-btn lg:hidden p-2 text-slate hover:text-white transition-colors"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="hidden sm:block">
              <h1 className="font-heading text-lg font-semibold text-white">
                {pageTitle}
              </h1>
              <p className="text-xs text-slate">
                {currentPortal.label}
              </p>
            </div>
          </div>
          
          <div className="flex-1 max-w-xl mx-4 lg:mx-6 hidden md:block">
            <GlobalSearchBar 
              placeholder="Search CXOs, briefs, articles..."
              className="w-full"
            />
          </div>
          
          <div className="flex items-center gap-4">
            <CurrencySelector />
            <button className="relative p-2 text-slate hover:text-white transition-colors">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-teal rounded-full" />
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6 pt-4">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
